import { Injectable, TemplateRef, Type, inject, Injector } from '@angular/core';
import { Overlay, OverlayRef, PositionStrategy } from '@angular/cdk/overlay';
import { ComponentPortal, TemplatePortal } from '@angular/cdk/portal';
import { ComponentRef } from '@angular/core';
import { TooltipComponent, TOOLTIP_CONTENT, TOOLTIP_DATA, TOOLTIP_TEMPLATE } from '@infinitform/shared-ui';

export interface TooltipConfig {
  /** The content to display in the tooltip */
  content: string | TemplateRef<any> | Type<any>;
  /** Position of the tooltip relative to the trigger element */
  position?: 'top' | 'bottom' | 'left' | 'right' | 'top-start' | 'top-end' | 'bottom-start' | 'bottom-end' | 'left-start' | 'left-end' | 'right-start' | 'right-end';
  /** Delay before showing the tooltip (in milliseconds) */
  showDelay?: number;
  /** Delay before hiding the tooltip (in milliseconds) */
  hideDelay?: number;
  /** Whether the tooltip should be disabled */
  disabled?: boolean;
  /** Custom CSS classes to apply to the tooltip */
  panelClass?: string | string[];
  /** Whether the tooltip should close when clicking outside */
  closeOnClickOutside?: boolean;
  /** Maximum width of the tooltip */
  maxWidth?: string;
  /** Data to pass to the tooltip component if using a component type */
  data?: any;
}

export interface TooltipRef {
  /** Close the tooltip */
  close(): void;
  /** Whether the tooltip is currently open */
  isOpen(): boolean;
  /** Update the tooltip content */
  updateContent(content: string | TemplateRef<any> | Type<any>): void;
}

@Injectable({
  providedIn: 'root'
})
export class TooltipService {
  private overlay = inject(Overlay);

  private activeTooltips = new Map<HTMLElement, TooltipRef>();

  /**
   * Show a tooltip for the given element
   */
  show(element: HTMLElement, config: TooltipConfig, viewContainerRef?: any): TooltipRef {
    // Close any existing tooltip for this element
    this.hide(element);

    const overlayRef = this.createOverlay(element, config);
    const tooltipRef = this.attachTooltip(overlayRef, config, viewContainerRef);

    const tooltipWrapper: TooltipRef = {
      close: () => this.closeTooltip(element, overlayRef),
      isOpen: () => overlayRef.hasAttached(),
      updateContent: (content) => this.updateTooltipContent(overlayRef, content, config, viewContainerRef)
    };

    this.activeTooltips.set(element, tooltipWrapper);

    return tooltipWrapper;
  }

  /**
   * Hide the tooltip for the given element
   */
  hide(element: HTMLElement): void {
    const tooltipRef = this.activeTooltips.get(element);
    if (tooltipRef) {
      tooltipRef.close();
      this.activeTooltips.delete(element);
    }
  }

  /**
   * Hide all active tooltips
   */
  hideAll(): void {
    this.activeTooltips.forEach(tooltipRef => tooltipRef.close());
    this.activeTooltips.clear();
  }

  private createOverlay(element: HTMLElement, config: TooltipConfig): OverlayRef {
    const positionStrategy = this.getPositionStrategy(element, config.position || 'top');
    
    const overlayConfig = {
      positionStrategy,
      panelClass: this.getPanelClasses(config.panelClass),
      hasBackdrop: false,
      scrollStrategy: this.overlay.scrollStrategies.reposition()
    };

    return this.overlay.create(overlayConfig);
  }

  private attachTooltip(overlayRef: OverlayRef, config: TooltipConfig, viewContainerRef?: any): ComponentRef<any> {
    let componentRef: ComponentRef<any>;

    if (typeof config.content === 'string') {
      // String content - use the tooltip component
      const tooltipComponent = this.getTooltipComponent();
      const injector = this.createInjector(config.content, null, config.data);
      componentRef = overlayRef.attach(new ComponentPortal(tooltipComponent, null, injector));
    } else if (config.content instanceof TemplateRef) {
      const tooltipComponent = this.getTooltipComponent();
      const injector = this.createInjector(null, config.content, config.data);
      componentRef = overlayRef.attach(new ComponentPortal(tooltipComponent, viewContainerRef, injector));
    } else {
      // Component content
      const injector = this.createInjector(null, null, config.data);
      componentRef = overlayRef.attach(new ComponentPortal(config.content, null, injector));
    }

    return componentRef;
  }

  private getPositionStrategy(element: HTMLElement, position: string): PositionStrategy {
    const positions = this.getPositionConfig(position);
    
    return this.overlay
      .position()
      .flexibleConnectedTo(element)
      .withPositions(positions)
      .withPush(true)
      .withViewportMargin(8);
  }

  private getPositionConfig(position: string): any {
    const baseOffset = 8;
    
    switch (position) {
      case 'top':
        return [
          { originX: 'center', originY: 'top', overlayX: 'center', overlayY: 'bottom', offsetY: -baseOffset, panelClass: 'tooltip-top' },
          { originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top', offsetY: baseOffset, panelClass: 'tooltip-bottom' }
        ];
      case 'bottom':
        return [
          { originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top', offsetY: baseOffset, panelClass: 'tooltip-bottom' },
          { originX: 'center', originY: 'top', overlayX: 'center', overlayY: 'bottom', offsetY: -baseOffset, panelClass: 'tooltip-top' }
        ];
      case 'left':
        return [
          { originX: 'start', originY: 'center', overlayX: 'end', overlayY: 'center', offsetX: -baseOffset, panelClass: 'tooltip-left' },
          { originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'center', offsetX: baseOffset, panelClass: 'tooltip-right' }
        ];
      case 'right':
        return [
          { originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'center', offsetX: baseOffset, panelClass: 'tooltip-right' },
          { originX: 'start', originY: 'center', overlayX: 'end', overlayY: 'center', offsetX: -baseOffset, panelClass: 'tooltip-left' }
        ];
      case 'top-start':
        return [
          { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -baseOffset, panelClass: 'tooltip-top' },
          { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: baseOffset, panelClass: 'tooltip-bottom' }
        ];
      case 'top-end':
        return [
          { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -baseOffset, panelClass: 'tooltip-top' },
          { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: baseOffset, panelClass: 'tooltip-bottom' }
        ];
      case 'bottom-start':
        return [
          { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: baseOffset, panelClass: 'tooltip-bottom' },
          { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -baseOffset, panelClass: 'tooltip-top' }
        ];
      case 'bottom-end':
        return [
          { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: baseOffset, panelClass: 'tooltip-bottom' },
          { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -baseOffset, panelClass: 'tooltip-top' }
        ];
      case 'left-start':
        return [
          { originX: 'start', originY: 'top', overlayX: 'end', overlayY: 'top', offsetX: -baseOffset, panelClass: 'tooltip-left' },
          { originX: 'end', originY: 'top', overlayX: 'start', overlayY: 'top', offsetX: baseOffset, panelClass: 'tooltip-right' }
        ];
      case 'left-end':
        return [
          { originX: 'start', originY: 'bottom', overlayX: 'end', overlayY: 'bottom', offsetX: -baseOffset, panelClass: 'tooltip-left' },
          { originX: 'end', originY: 'bottom', overlayX: 'start', overlayY: 'bottom', offsetX: baseOffset, panelClass: 'tooltip-right' }
        ];
      case 'right-start':
        return [
          { originX: 'end', originY: 'top', overlayX: 'start', overlayY: 'top', offsetX: baseOffset, panelClass: 'tooltip-right' },
          { originX: 'start', originY: 'top', overlayX: 'end', overlayY: 'top', offsetX: -baseOffset, panelClass: 'tooltip-left' }
        ];
      case 'right-end':
        return [
          { originX: 'end', originY: 'bottom', overlayX: 'start', overlayY: 'bottom', offsetX: baseOffset, panelClass: 'tooltip-right' },
          { originX: 'start', originY: 'bottom', overlayX: 'end', overlayY: 'bottom', offsetX: -baseOffset, panelClass: 'tooltip-left' }
        ];
      default:
        return this.getPositionConfig('top');
    }
  }

  private getPanelClasses(panelClass?: string | string[]): string[] {
    const classes = ['tooltip-overlay'];
    
    if (panelClass) {
      if (typeof panelClass === 'string') {
        classes.push(panelClass);
      } else {
        classes.push(...panelClass);
      }
    }
    
    return classes;
  }

  private createInjector(content: string | null, template: TemplateRef<any> | null, data: any): Injector {
    return Injector.create({
      providers: [
        {
          provide: TOOLTIP_CONTENT,
          useValue: content
        },
        {
          provide: TOOLTIP_TEMPLATE,
          useValue: template
        },
        {
          provide: TOOLTIP_DATA,
          useValue: data
        }
      ]
    });
  }

  private getTooltipComponent() {
    return TooltipComponent;
  }

  private closeTooltip(element: HTMLElement, overlayRef: OverlayRef): void {
    overlayRef.detach();
    overlayRef.dispose();
    this.activeTooltips.delete(element);
  }

  private updateTooltipContent(overlayRef: OverlayRef, content: string | TemplateRef<any> | Type<any>, config: TooltipConfig, viewContainerRef?: any): void {
    if (overlayRef.hasAttached()) {
      overlayRef.detach();
      this.attachTooltip(overlayRef, { ...config, content }, viewContainerRef);
    }
  }
}
